import { useEffect, useRef, useState } from "react";
import {
  Bookmark,
  Check,
  ChevronDown,
  Pencil,
  Trash2,
} from "lucide-react";
import { feedViewIcon } from "../../Center/Feeds/feedConstants";

// Universal lens dropdown (né MapPresetControl — Phase 8, GS_GRAPH_DESIGN.md §11).
// Consumed by the MAP, the GS GRAPH and the FEED; change with all pages in mind.
// A dropdown to select a saved lens. Save and New moved OUT to icon-only bar
// buttons beside the trigger (SIDEBAR_LENS_REWORK, birr 2026-08-21 —
// supersedes the 08-19 menu-footer ruling): this menu only picks, edits and
// deletes; LensShell owns the Save/New buttons and their handlers.
export default function LensControl({
  presets = [],
  activePresetId,
  isDraft = false,
  isModified = false,
  onApply,
  onEdit,
  onDelete,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);

  const activePreset = presets.find((preset) => preset.id === activePresetId) || null;

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const label = isDraft ? "Unnamed" : activePreset ? activePreset.name : "Lenses";
  const ActiveIcon =
    activePreset && !isDraft ? feedViewIcon(activePreset.icon) : null;

  // Small "LIVE" pulse tag for dynamic presets (Trending) — signals its filters
  // are computed live rather than a saved snapshot.
  const LiveTag = () => (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-sky-400/40 bg-sky-400/10 px-1.5 py-px text-[8px] font-semibold uppercase tracking-[0.12em] text-sky-300">
      <span className="relative flex h-1.5 w-1.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-sky-400 opacity-75" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-sky-400" />
      </span>
      Live
    </span>
  );

  return (
    <div className="flex shrink-0 items-center">
      {/* Dropdown: pick a saved preset */}
      <div ref={rootRef} className="relative">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-mainDivider bg-mainButtonBg/35 px-2.5 text-xs text-mainText transition-all duration-200 hover:bg-mainButtonBg/60 hover:text-velBlue focus:outline-none focus-visible:ring-2 focus-visible:ring-inputFocus/60"
          title="Filter lenses"
        >
          {ActiveIcon ? (
            <ActiveIcon
              size={13}
              style={{ color: activePreset.color || "#38bdf8" }}
            />
          ) : (
            <Bookmark size={12} className="text-velBlue/80" />
          )}
          <span className="max-w-[120px] truncate">{label}</span>
          {activePreset?.dynamic && !isDraft && <LiveTag />}
          {activePreset && isModified && !isDraft && !activePreset.dynamic && (
            <span className="text-sm leading-none text-amber-400" title="Unsaved changes">
              •
            </span>
          )}
          <ChevronDown
            size={12}
            className={`transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>

        {open && (
          <div className="absolute left-0 top-[calc(100%+6px)] z-[1200] w-64 overflow-hidden rounded-xl border border-mainDivider bg-mainBg/95 p-1.5 shadow-[0_0_0_1px_rgba(14,165,233,0.06),0_22px_48px_rgba(0,0,0,0.35)] backdrop-blur-md">
            <div className="max-h-64 overflow-y-auto">
              {presets.length === 0 && (
                <div className="px-2 py-3 text-center text-[11px] text-mutedText">
                  No saved lenses yet.
                </div>
              )}
              {presets.map((preset) => {
                const active = preset.id === activePresetId;
                const PresetIcon = feedViewIcon(preset.icon);
                return (
                  <div
                    key={preset.id}
                    className={`group flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs transition ${
                      active
                        ? "bg-velBlue/10 text-mainText"
                        : "text-mutedText hover:bg-mainButtonBg/45 hover:text-mainText"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        onApply?.(preset.id);
                        setOpen(false);
                      }}
                      className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    >
                      <PresetIcon
                        size={13}
                        className="shrink-0"
                        style={{ color: preset.color || "#38bdf8" }}
                      />
                      <span className="truncate">{preset.name}</span>
                      {preset.dynamic && <LiveTag />}
                      {active && (
                        <Check size={12} className="ml-auto shrink-0 text-velBlue" />
                      )}
                    </button>
                    {!preset.fixed && (
                      <>
                        <button
                          type="button"
                          aria-label={`Edit ${preset.name}`}
                          title="Edit lens"
                          onClick={() => {
                            onEdit?.(preset);
                            setOpen(false);
                          }}
                          className="shrink-0 rounded p-1 text-mutedText opacity-0 transition hover:text-velBlue group-hover:opacity-100"
                        >
                          <Pencil size={12} />
                        </button>
                        <button
                          type="button"
                          aria-label={`Delete ${preset.name}`}
                          title="Delete lens"
                          onClick={() => {
                            onDelete?.(preset);
                            setOpen(false);
                          }}
                          className="shrink-0 rounded p-1 text-mutedText opacity-0 transition hover:text-red-400 group-hover:opacity-100"
                        >
                          <Trash2 size={12} />
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
